import * as React from 'react';
import { useGetAllContainerTypeGroupsQuery } from '../../graphql/queries/getAllContainerTypeGroups.generated';
import { CustomTable } from '../../components/Table';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { useDeleteContainerTypeGroupMutation } from '../../graphql/mutations/deleteContainerTypeGroup.generated';
import { LayoutStoreInstance } from '../../LayoutStore';
import { useCreateContainerTypeGroupMutation } from '../../graphql/mutations/createContainerTypeGroup.generated';
import { CustomModal } from '../../components/CustomModal';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import i18n from '@/utils/i18n';
import { Button } from 'reactstrap';
import { useUpdateContainerTypeGroupMutation } from '../../graphql/mutations/updateContainerTypeGroup.generated';
import TryNewDesignBanner from './TryNewDesignBanner';
import { Routes } from '../../utils/routes';

export default function EditContainerTypeGroups() {
  const [newName, setNewName] = React.useState('');
  const [category, setCategory] = React.useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = React.useState<number>(-1);
  const [
    addNewContainerTypeGroupModalOpen,
    setAddNewContainerTypeGroupModalOpen,
  ] = React.useState(false);
  const [editContainerTypeGroupModalOpen, setEditContainerTypeGroupModalOpen] =
    React.useState(false);

  const {
    data: containerTypeGroups,
    loading,
    error,
  } = useGetAllContainerTypeGroupsQuery({
    fetchPolicy: 'no-cache',
  });

  const [deleteGroup, { loading: deleting }] =
    useDeleteContainerTypeGroupMutation({
      refetchQueries: ['GetAllContainerTypeGroups'],
      onError: (e) => {
        LayoutStoreInstance.setError(e.message);
      },
    });

  const [createGroup, { loading: create }] =
    useCreateContainerTypeGroupMutation({
      refetchQueries: ['GetAllContainerTypeGroups'],
      onError: (e) => {
        LayoutStoreInstance.setError(e.message);
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess(
          i18n.t('EditContainerTypeGroups:groupCreated'),
        );
        setNewName('');
      },
    });

  const [updateGroup, { loading: update }] =
    useUpdateContainerTypeGroupMutation({
      refetchQueries: ['GetAllContainerTypeGroups'],
      onError: (e) => {
        LayoutStoreInstance.setError(e.message);
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess(
          i18n.t('EditContainerTypeGroups:groupUpdated'),
        );
      },
    });

  React.useEffect(() => {
    if (
      !addNewContainerTypeGroupModalOpen &&
      !editContainerTypeGroupModalOpen
    ) {
      setNewName('');
      setEditingGroupId(-1);
      setCategory(null);
    }
  }, [addNewContainerTypeGroupModalOpen, editContainerTypeGroupModalOpen]);

  console.log({ editingGroupId });
  return (
    <div>
      <TryNewDesignBanner
        title={i18n.t('EditContainerTypeGroups:newDesignBannerTitle')}
        text={i18n.t('EditContainerTypeGroups:newDesignBannerText')}
        cta={i18n.t('EditContainerTypeGroups:tryNewDesign')}
        targetRoute={Routes.editContainerTypeGroupsBeta()}
      />
      <h1>{i18n.t('EditContainerTypeGroups:editContainerTypeGroups')}</h1>
      <Button
        className="float-end mb-2"
        onClick={() => setAddNewContainerTypeGroupModalOpen(true)}
      >
        <FaPlus />
      </Button>
      <CustomTable
        error={error}
        loading={loading}
        headers={[
          i18n.t('EditContainerTypeGroups:groupName'),
          i18n.t('EditContainerTypeGroups:category'),
          i18n.t('EditContainerTypeGroups:hasLinkedStores'),
          i18n.t('EditContainerTypeGroups:containerTypes'),
          '',
        ]}
        hover
        striped
        rows={(containerTypeGroups?.getAllContainerTypeGroups || []).map(
          (group) => (
            <tr key={group.id}>
              <td>
                <Button
                  className="me-1"
                  style={{ border: 'none', background: 'none' }}
                  onClick={() => {
                    setEditingGroupId(group.id);
                    setNewName(group.name || '');
                    setCategory(group.category ?? null);
                    setEditContainerTypeGroupModalOpen(true);
                  }}
                >
                  <FaEdit />
                </Button>
                {group.name}
              </td>
              <td>
                {group.category === 'Bowls'
                  ? i18n.t('EditContainerTypeGroups:categoryBowls')
                  : group.category === 'Cups'
                    ? i18n.t('EditContainerTypeGroups:categoryCups')
                    : i18n.t('EditContainerTypeGroups:categoryNone')}
              </td>
              <td>
                {group.has_linked_stores
                  ? i18n.t('Common:yes')
                  : i18n.t('Common:no')}
              </td>
              <td>
                {group.container_types.map((type) => (
                  <div key={type.id} className="mb-1">
                    <img
                      src={type.image_url || undefined}
                      alt={type.name}
                      style={{
                        width: '50px',
                        height: '50px',
                      }}
                    />
                    {type.name}
                  </div>
                ))}
              </td>
              <td>
                <button
                  onClick={() => {
                    deleteGroup({
                      variables: {
                        id: group.id,
                      },
                    });
                  }}
                  style={{ border: 'none', background: 'none', padding: 0 }}
                  disabled={
                    group.has_linked_stores ||
                    group.container_types.length > 0 ||
                    deleting
                  }
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ),
        )}
      />
      <CustomModal
        acceptButtonAction={() => createGroup({ variables: { name: newName } })}
        acceptButtonDisabled={isNullOrWhitespace(newName) || create}
        acceptButtonText={i18n.t('Common:create')}
        acceptButtonIcon={<></>}
        body={
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('EditContainerTypeGroups:groupName')}
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              onChange={(e) => setNewName(e.target.value)}
              value={newName}
            />
          </div>
        }
        isOpen={addNewContainerTypeGroupModalOpen}
        header={i18n.t('EditContainerTypeGroups:addNewGroup')}
        setIsOpen={setAddNewContainerTypeGroupModalOpen}
      />
      <CustomModal
        acceptButtonAction={() =>
          updateGroup({
            variables: {
              id: editingGroupId,
              payload: {
                ...(isNullOrWhitespace(newName) ? {} : { name: newName }),
                category: category,
              },
            },
          })
        }
        acceptButtonDisabled={update}
        acceptButtonText={i18n.t('Common:update')}
        acceptButtonIcon={<></>}
        body={
          <>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('EditContainerTypeGroups:groupName')}
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                onChange={(e) => setNewName(e.target.value)}
                value={newName}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="category-addon1">
                  {i18n.t('EditContainerTypeGroups:category')}
                </span>
              </div>
              <select
                className="form-select"
                aria-label="Category"
                onChange={(e) =>
                  setCategory(e.target.value === '' ? null : e.target.value)
                }
                value={category ?? ''}
              >
                <option value="">
                  {i18n.t('EditContainerTypeGroups:categoryNone')}
                </option>
                <option value="Bowls">
                  {i18n.t('EditContainerTypeGroups:categoryBowls')}
                </option>
                <option value="Cups">
                  {i18n.t('EditContainerTypeGroups:categoryCups')}
                </option>
              </select>
            </div>
          </>
        }
        isOpen={editContainerTypeGroupModalOpen}
        header={i18n.t('EditContainerTypeGroups:updateGroup')}
        setIsOpen={setEditContainerTypeGroupModalOpen}
      />
    </div>
  );
}
